<template>
  <div class="space-y-6">
    <!-- Header with Create Button -->
    <div class="flex items-center justify-between">
      <div>
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Custom Models (BYOM)</h3>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Bring your own model endpoints with encrypted API key storage
        </p>
      </div>
      <Button variant="primary" @click="handleCreate">
        <svg
          class="w-4 h-4 mr-1.5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 4v16m8-8H4"
          />
        </svg>
        Create Custom Model
      </Button>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <LoadingSpinner size="lg" />
    </div>

    <!-- Error State -->
    <Alert v-else-if="error" variant="error" class="mb-4">
      {{ error }}
    </Alert>

    <!-- Empty State -->
    <!-- eslint-disable-next-line asab-design-system/no-raw-button-card-markup -- dashed empty-state box, not a Card (no dashed/empty variant exists yet) -->
    <div
      v-else-if="customModels.length === 0"
      class="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700"
    >
      <svg
        class="mx-auto h-12 w-12 text-gray-400"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
        />
      </svg>
      <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">No custom models</h3>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Create a model directly or import from a connection.
      </p>
      <div class="mt-6 flex items-center justify-center space-x-4">
        <Button variant="primary" @click="handleCreate">
          Create Directly
        </Button>
        <Button variant="outline" @click="$emit('create-connection')">
          Manage Connections
        </Button>
      </div>
    </div>

    <!-- Models Grid -->
    <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <CustomModelCard
        v-for="model in customModels"
        :key="model.slug"
        :model="model"
        @edit="handleEdit"
        @delete="handleDelete"
      />
    </div>

    <!-- Custom Model Form Modal -->
    <CustomModelForm
      :open="isFormOpen"
      :edit-model="editingModel"
      @update:open="isFormOpen = $event"
      @created="handleCreated"
      @updated="handleUpdated"
      @cancel="handleFormCancel"
    />

    <!-- Confirm Delete Modal -->
    <ConfirmDeleteModal
      :open="isDeleteModalOpen"
      :model="deletingModel"
      model-type="custom"
      @update:open="isDeleteModalOpen = $event"
      @confirm="handleDeleteConfirm"
      @cancel="handleDeleteCancel"
    />
  </div>
</template>

<script setup lang="ts">
  import { ref, onMounted } from 'vue';
  import { Button, LoadingSpinner, Alert } from '@asabdev/asab-ui';
  import { useNotification } from '@asabdev/asab-ui';
  import CustomModelCard from './CustomModelCard.vue';
  import CustomModelForm from './CustomModelForm.vue';
  import ConfirmDeleteModal from './ConfirmDeleteModal.vue';
  import { useCustomModels } from '../composables/useCustomModels';
  import type { CustomModelResponse, CreateCustomModelRequest, UpdateCustomModelRequest } from '../types';

  interface Emits {
    (e: 'edit', model: CustomModelResponse): void;
    (e: 'delete', model: CustomModelResponse): void;
    (e: 'create'): void;
    (e: 'create-connection'): void;
  }

  const emit = defineEmits<Emits>();

  // Composables
  const {
    customModels,
    isLoading,
    error,
    fetchCustomModels,
    createCustomModel,
    updateCustomModel,
    deleteCustomModel,
  } = useCustomModels();

  const { showSuccess, showError } = useNotification();

  // Local state
  const isFormOpen = ref(false);
  const isDeleteModalOpen = ref(false);
  const editingModel = ref<CustomModelResponse | null>(null);
  const deletingModel = ref<CustomModelResponse | null>(null);

  // Methods
  const handleCreate = () => {
    editingModel.value = null;
    isFormOpen.value = true;
    emit('create');
  };

  const handleEdit = (model: CustomModelResponse) => {
    editingModel.value = model;
    isFormOpen.value = true;
    emit('edit', model);
  };

  const handleDelete = (model: CustomModelResponse) => {
    deletingModel.value = model;
    isDeleteModalOpen.value = true;
    emit('delete', model);
  };

  const handleCreated = async (createRequest: CreateCustomModelRequest) => {
    try {
      const result = await createCustomModel(createRequest);
      
      if (result) {
        showSuccess(`Successfully created custom model "${result.name}"`);
        isFormOpen.value = false;
        editingModel.value = null;
        // Refresh the list
        await fetchCustomModels();
      } else {
        showError('Failed to create custom model. Please try again.');
      }
    } catch (err) {
      showError('An error occurred while creating the custom model.');
      console.error('Error creating custom model:', err);
    }
  };

  const handleUpdated = async (updateRequest: UpdateCustomModelRequest) => {
    if (!editingModel.value) return;

    try {
      const result = await updateCustomModel(editingModel.value.slug, updateRequest);
      
      if (result) {
        showSuccess(`Successfully updated custom model "${result.name}"`);
        isFormOpen.value = false;
        editingModel.value = null;
        // Refresh the list
        await fetchCustomModels();
      } else {
        showError('Failed to update custom model. Please try again.');
      }
    } catch (err) {
      showError('An error occurred while updating the custom model.');
      console.error('Error updating custom model:', err);
    }
  };

  const handleFormCancel = () => {
    isFormOpen.value = false;
    editingModel.value = null;
  };

  const handleDeleteConfirm = async () => {
    if (!deletingModel.value) return;

    try {
      const success = await deleteCustomModel(deletingModel.value.slug);
      
      if (success) {
        showSuccess(`Successfully deleted custom model "${deletingModel.value.name}"`);
        isDeleteModalOpen.value = false;
        deletingModel.value = null;
        // Refresh the list
        await fetchCustomModels();
      } else {
        showError('Failed to delete custom model. Please try again.');
      }
    } catch (err) {
      showError('An error occurred while deleting the custom model.');
      console.error('Error deleting custom model:', err);
    }
  };

  const handleDeleteCancel = () => {
    isDeleteModalOpen.value = false;
    deletingModel.value = null;
  };

  // Lifecycle
  onMounted(async () => {
    await fetchCustomModels();
  });
</script>
